import { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { DollarSign, Receipt, Download, CreditCard, TrendingUp, Search, Building2, AlertCircle, FileDown, Crown, Zap, Star, Check, Loader2, XCircle } from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const statusColors = {
  DRAFT: "status-draft",
  SENT: "status-paused",
  PAID: "status-active",
  FAILED: "text-destructive bg-destructive/10 border-destructive/30"
};

export default function Billing() {
  const { token, isAdmin } = useAuth();
  const [searchParams] = useSearchParams();
  const [balance, setBalance] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [adminOverview, setAdminOverview] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);

  // Payment methods state
  const [paymentMethodsLoading, setPaymentMethodsLoading] = useState(false);

  useEffect(() => {
    fetchBillingData();
  }, []);

  const fetchBillingData = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      if (isAdmin) {
        // Admin view - accounts receivable
        const overviewRes = await axios.get(`${API}/admin/billing/overview`, { headers });
        setAdminOverview(overviewRes.data);
      } else {
        // Advertiser view
        const [balanceRes, invoicesRes] = await Promise.all([
          axios.get(`${API}/billing/balance`, { headers }),
          axios.get(`${API}/billing/invoices`, { headers })
        ]);
        setBalance(balanceRes.data);
        setInvoices(invoicesRes.data);
      }
    } catch (error) {
      console.error("Failed to fetch billing data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleManagePaymentMethods = async () => {
    setPaymentMethodsLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const returnUrl = `${window.location.origin}/billing`;
      
      const response = await axios.post(`${API}/subscriptions/manage-payment-methods`, {
        return_url: returnUrl
      }, { headers });
      
      // Open Stripe Customer Portal in a new tab so the user can return without losing context
      if (response.data.portal_url) {
        window.open(response.data.portal_url, '_blank', 'noopener,noreferrer');
      }
    } catch (error) {
      console.error("Failed to open payment portal:", error);
      toast.error(error.response?.data?.detail || "Failed to open payment management");
    } finally {
      setPaymentMethodsLoading(false);
    }
  };

  // Export single invoice as CSV
  const exportInvoiceCSV = (invoice) => {
    const headers = ["Metric Type", "Campaign", "Quantity", "Unit Price", "Subtotal"];
    const rows = invoice.line_items.map(item => [
      item.metric_type,
      item.campaign_name || "N/A",
      item.quantity,
      `$${item.unit_price?.toFixed(4) || '0.0000'}`,
      `$${item.subtotal?.toFixed(2) || item.amount?.toFixed(2) || '0.00'}`
    ]);
    
    // Add invoice summary
    rows.push([]);
    rows.push(["Invoice Number", invoice.invoice_number || invoice.id]);
    rows.push(["Date", format(new Date(invoice.created_at), "MMM d, yyyy")]);
    rows.push(["Status", invoice.status]);
    rows.push(["Total", `$${(invoice.amount || invoice.total || 0).toFixed(2)} ${invoice.currency || 'AUD'}`]);
    
    const csvContent = [headers.join(","), ...rows.map(row => row.join(","))].join("\n");
    downloadCSV(csvContent, `invoice_${invoice.invoice_number || invoice.id.slice(0, 8)}_${format(new Date(invoice.created_at), "yyyy-MM-dd")}.csv`);
    toast.success("Invoice exported successfully");
  };

  // Export all invoices as CSV
  const exportAllInvoicesCSV = () => {
    if (invoices.length === 0) {
      toast.error("No invoices to export");
      return;
    }
    
    const headers = ["Invoice Number", "Date", "Due Date", "Status", "Amount", "Currency", "Description"];
    const rows = invoices.map(invoice => {
      return [
        invoice.invoice_number || invoice.id,
        format(new Date(invoice.created_at), "yyyy-MM-dd"),
        invoice.due_date ? format(new Date(invoice.due_date), "yyyy-MM-dd") : "N/A",
        invoice.status,
        (invoice.amount || invoice.total || 0).toFixed(2),
        invoice.currency || 'AUD',
        `"${(invoice.description || '').replace(/"/g, '""')}"`
      ];
    });
    
    const csvContent = [headers.join(","), ...rows.map(row => row.join(","))].join("\n");
    downloadCSV(csvContent, `all_invoices_${format(new Date(), "yyyy-MM-dd")}.csv`);
    toast.success(`${invoices.length} invoices exported successfully`);
  };

  // Export full billing data (invoices + ledger) via backend
  const [exportingFullData, setExportingFullData] = useState(false);
  const exportFullBillingData = async () => {
    setExportingFullData(true);
    try {
      const response = await axios.get(`${API}/billing/export/csv`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `booma_billing_full_${format(new Date(), 'yyyy-MM-dd')}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Full billing data exported", {
        description: "Includes invoices and ledger entries"
      });
    } catch (error) {
      console.error("Failed to export billing data:", error);
      toast.error("Failed to export billing data");
    } finally {
      setExportingFullData(false);
    }
  };

  // Helper to download CSV file
  const downloadCSV = (content, filename) => {
    const blob = new Blob([content], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  // Export admin accounts receivable as CSV
  const exportAccountsCSV = () => {
    if (!adminOverview?.accounts?.length) {
      toast.error("No accounts to export");
      return;
    }
    
    const headers = ["Business Name", "Tier", "Status", "Total Billed", "Total Paid", "Outstanding", "Invoice Count"];
    const rows = adminOverview.accounts.map(acc => [
      `"${acc.business_name || ''}"`,
      acc.tier || '',
      acc.status || '',
      acc.total_billable?.toFixed(2) || '0.00',
      acc.total_paid?.toFixed(2) || '0.00',
      acc.outstanding?.toFixed(2) || '0.00',
      acc.invoice_count || 0
    ]);
    
    // Add summary row
    rows.push([]);
    rows.push(["SUMMARY"]);
    rows.push(["Total Billed", "", "", adminOverview.summary.total_billed?.toFixed(2)]);
    rows.push(["Total Paid", "", "", "", adminOverview.summary.total_paid?.toFixed(2)]);
    rows.push(["Total Outstanding", "", "", "", "", adminOverview.summary.total_outstanding?.toFixed(2)]);
    
    const csvContent = [headers.join(","), ...rows.map(row => row.join(","))].join("\n");
    downloadCSV(csvContent, `accounts_receivable_${format(new Date(), "yyyy-MM-dd")}.csv`);
    toast.success(`${adminOverview.accounts.length} accounts exported successfully`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-pulse text-primary">Loading billing data...</div>
      </div>
    );
  }

  // Admin View - Accounts Receivable
  if (isAdmin && adminOverview) {
    const filteredAccounts = adminOverview.accounts.filter(acc => 
      !searchQuery || 
      acc.business_name?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    return (
      <div className="space-y-6 animate-fade-in" data-testid="billing-page-admin">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-4xl font-black tracking-tight">Accounts Receivable</h1>
            <p className="text-muted-foreground mt-1">
              Billing overview for all advertisers
            </p>
          </div>
          <Button 
            variant="outline" 
            onClick={exportAccountsCSV}
            data-testid="export-accounts-btn"
          >
            <FileDown className="mr-2 h-4 w-4" />
            Export CSV
          </Button>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="bg-card border-border">
            <CardContent className="p-6">
              <p className="text-xs text-muted-foreground uppercase tracking-wider">Total Billed</p>
              <p className="text-3xl font-bold mt-2 text-primary">${adminOverview.summary.total_billed?.toLocaleString()}</p>
            </CardContent>
          </Card>
          <Card className="bg-card border-border">
            <CardContent className="p-6">
              <p className="text-xs text-muted-foreground uppercase tracking-wider">Total Paid</p>
              <p className="text-3xl font-bold mt-2 text-green-500">${adminOverview.summary.total_paid?.toLocaleString()}</p>
            </CardContent>
          </Card>
          <Card className="bg-card border-border">
            <CardContent className="p-6">
              <p className="text-xs text-muted-foreground uppercase tracking-wider">Outstanding</p>
              <p className={`text-3xl font-bold mt-2 ${adminOverview.summary.total_outstanding > 0 ? 'text-amber-500' : 'text-green-500'}`}>
                ${Math.abs(adminOverview.summary.total_outstanding)?.toLocaleString()}
              </p>
            </CardContent>
          </Card>
          <Card className="bg-card border-border">
            <CardContent className="p-6">
              <p className="text-xs text-muted-foreground uppercase tracking-wider">Accounts</p>
              <p className="text-3xl font-bold mt-2">{adminOverview.summary.account_count}</p>
            </CardContent>
          </Card>
        </div>

        {/* Accounts Table */}
        <Card className="bg-card border-border">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Building2 className="h-5 w-5" />
                Advertiser Accounts
              </CardTitle>
              <div className="relative w-64">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search accounts..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Business</TableHead>
                  <TableHead>Tier</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Billed</TableHead>
                  <TableHead className="text-right">Paid</TableHead>
                  <TableHead className="text-right">Outstanding</TableHead>
                  <TableHead className="text-right">Invoices</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredAccounts.map(account => (
                  <TableRow key={account.account_id}>
                    <TableCell className="font-medium">{account.business_name}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-xs capitalize">{account.tier?.toLowerCase()}</Badge>
                    </TableCell>
                    <TableCell>
                      <Badge 
                        variant="outline" 
                        className={`text-xs ${account.status === 'ACTIVE' ? 'text-green-500' : account.status === 'SUSPENDED' ? 'text-red-500' : 'text-yellow-500'}`}
                      >
                        {account.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">${account.total_billable?.toFixed(2)}</TableCell>
                    <TableCell className="text-right text-green-500">${account.total_paid?.toFixed(2)}</TableCell>
                    <TableCell className={`text-right font-semibold ${account.outstanding > 0 ? 'text-amber-500' : 'text-green-500'}`}>
                      ${account.outstanding?.toFixed(2)}
                    </TableCell>
                    <TableCell className="text-right text-muted-foreground">{account.invoice_count}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Advertiser View (existing code)
  const totalSpend = invoices.reduce((sum, inv) => sum + inv.total, 0);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="billing-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-4xl font-black tracking-tight">Billing</h1>
          <p className="text-muted-foreground mt-1">
            Manage invoices and payment methods
          </p>
        </div>
        <Button 
          variant="outline" 
          onClick={handleManagePaymentMethods}
          disabled={paymentMethodsLoading}
          data-testid="add-payment-btn"
        >
          {paymentMethodsLoading ? (
            <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Opening...</>
          ) : (
            <><CreditCard className="mr-2 h-4 w-4" />Manage Payment Methods</>
          )}
        </Button>
      </div>

      {/* Balance Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-card border-border" data-testid="current-spend">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Current Period Spend</p>
                <p className="text-3xl font-bold mt-2">${balance?.current_period_spend?.toLocaleString()}</p>
                <p className="text-xs text-muted-foreground mt-1">{balance?.currency}</p>
              </div>
              <div className="p-3 rounded-xl bg-primary/10">
                <DollarSign className="h-6 w-6 text-primary" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-card border-border">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Previous Balance</p>
                <p className="text-3xl font-bold mt-2">${balance?.previous_balance?.toLocaleString()}</p>
              </div>
              <div className="p-3 rounded-xl bg-yellow-500/10">
                <Receipt className="h-6 w-6 text-yellow-500" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-card border-border">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Credits</p>
                <p className="text-3xl font-bold mt-2">${balance?.credits?.toLocaleString()}</p>
              </div>
              <div className="p-3 rounded-xl bg-green-500/10">
                <TrendingUp className="h-6 w-6 text-green-500" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-card border-border">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Total Historical Spend</p>
                <p className="text-3xl font-bold mt-2">${totalSpend.toLocaleString()}</p>
              </div>
              <div className="p-3 rounded-xl bg-purple-500/10">
                <DollarSign className="h-6 w-6 text-purple-500" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Invoices Table */}
      <Card className="bg-card border-border">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Invoices</CardTitle>
              <CardDescription>Your billing history</CardDescription>
            </div>
            <div className="flex gap-2">
              {invoices.length > 0 && (
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={exportAllInvoicesCSV}
                  data-testid="export-all-invoices-btn"
                >
                  <FileDown className="mr-2 h-4 w-4" />
                  Export Invoices
                </Button>
              )}
              <Button 
                variant="outline" 
                size="sm" 
                onClick={exportFullBillingData}
                disabled={exportingFullData}
                data-testid="export-full-billing-btn"
              >
                {exportingFullData ? (
                  <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Exporting...</>
                ) : (
                  <><Download className="mr-2 h-4 w-4" />Export Full Report</>
                )}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Invoice</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {invoices.map(invoice => (
                  <TableRow key={invoice.id} data-testid={`invoice-row-${invoice.id}`}>
                    <TableCell>
                      <span className="font-mono text-sm">{invoice.invoice_number || invoice.id.slice(0, 8)}</span>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm">
                        {format(new Date(invoice.created_at), "MMM d, yyyy")}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={statusColors[invoice.status]}>
                        {invoice.status}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <span className="font-mono font-semibold">${(invoice.amount || invoice.total || 0).toLocaleString()}</span>
                      <span className="text-muted-foreground text-xs ml-1">{invoice.currency || 'AUD'}</span>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm text-muted-foreground truncate max-w-[200px]" title={invoice.description}>
                        {invoice.description || 'Invoice'}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => exportInvoiceCSV(invoice)}
                        title="Download invoice as CSV"
                        data-testid={`download-invoice-${invoice.id}`}
                      >
                        <Download className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {invoices.length === 0 && (
        <Card className="bg-card border-border border-dashed">
          <CardContent className="p-12 text-center">
            <Receipt className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="font-semibold text-lg mb-2">No invoices yet</h3>
            <p className="text-muted-foreground">Invoices will appear here once you start running campaigns</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
